import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

export interface DialCodeEntry {
  code: string
  label: string
  flag: string
}

export const DIAL_CODES: DialCodeEntry[] = [
  { code: '+48', label: 'PL', flag: '🇵🇱' },
  { code: '+49', label: 'DE', flag: '🇩🇪' },
  { code: '+44', label: 'GB', flag: '🇬🇧' },
  { code: '+33', label: 'FR', flag: '🇫🇷' },
  { code: '+39', label: 'IT', flag: '🇮🇹' },
  { code: '+34', label: 'ES', flag: '🇪🇸' },
  { code: '+31', label: 'NL', flag: '🇳🇱' },
  { code: '+32', label: 'BE', flag: '🇧🇪' },
  { code: '+43', label: 'AT', flag: '🇦🇹' },
  { code: '+41', label: 'CH', flag: '🇨🇭' },
  { code: '+46', label: 'SE', flag: '🇸🇪' },
  { code: '+47', label: 'NO', flag: '🇳🇴' },
  { code: '+45', label: 'DK', flag: '🇩🇰' },
  { code: '+358', label: 'FI', flag: '🇫🇮' },
  { code: '+420', label: 'CZ', flag: '🇨🇿' },
  { code: '+421', label: 'SK', flag: '🇸🇰' },
  { code: '+36', label: 'HU', flag: '🇭🇺' },
  { code: '+380', label: 'UA', flag: '🇺🇦' },
  { code: '+1', label: 'US/CA', flag: '🇺🇸' },
  { code: '+7', label: 'RU', flag: '🇷🇺' },
]

const DEFAULT_DIAL_BY_LOCALE: Record<string, string> = {
  pl: '+48',
  cs: '+420',
  en: '+48',
}

export function getDefaultDialCode(locale: string): string {
  const primary = locale.split(/[-_]/)[0]?.toLowerCase() ?? 'pl'
  return DEFAULT_DIAL_BY_LOCALE[primary] ?? '+48'
}

/** Extract dial code from a stored E.164 string, e.g. "+48123456789" → ["+48", "123456789"] */
function parsePhoneNumber(value: string, defaultDial: string): [string, string] {
  if (!value) return [defaultDial, '']
  for (const entry of DIAL_CODES) {
    if (value.startsWith(entry.code)) {
      return [entry.code, value.slice(entry.code.length)]
    }
  }
  // Unknown dial code — keep local digits, fall back to locale default for the select
  return [defaultDial, value.replace(/^\+\d+/, '')]
}

interface PhoneInputProps {
  value: string
  onChange: (e164: string) => void
  disabled?: boolean
  variant?: 'light' | 'dark'
  placeholder?: string
  dialCodeLabel?: string
}

export default function PhoneInput({
  value,
  onChange,
  disabled = false,
  variant = 'light',
  placeholder = '123 456 789',
  dialCodeLabel = 'Dial code',
}: PhoneInputProps) {
  const { i18n } = useTranslation()
  const defaultDial = getDefaultDialCode(i18n.language)
  const isDark = variant === 'dark'

  const [dialCode, setDialCode] = useState<string>(() => parsePhoneNumber(value, defaultDial)[0])
  const [localNumber, setLocalNumber] = useState<string>(() => parsePhoneNumber(value, defaultDial)[1])

  // Keep internal state in sync when the parent resets the value externally,
  // or when UI language changes and the field is still empty.
  useEffect(() => {
    const [parsedCode, parsedLocal] = parsePhoneNumber(value, defaultDial)
    setDialCode(parsedCode)
    setLocalNumber(parsedLocal)
  }, [value, defaultDial])

  const handleDialCodeChange = (code: string) => {
    setDialCode(code)
    const local = localNumber.replace(/[\s\-]/g, '')
    onChange(local ? code + local : '')
  }

  const handleLocalNumberChange = (raw: string) => {
    // Allow digits, spaces and hyphens only
    const sanitised = raw.replace(/[^\d\s\-]/g, '')
    setLocalNumber(sanitised)
    const digits = sanitised.replace(/[\s\-]/g, '')
    onChange(digits ? dialCode + digits : '')
  }

  const selectClass = isDark
    ? 'bg-[#1a1a1a] border border-gray-600 text-white focus:outline-none focus:ring-[#FF5A00] focus:border-[#FF5A00] disabled:opacity-50'
    : 'bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-[#212121] focus:border-[#212121] disabled:opacity-50'

  const inputClass = isDark
    ? 'block w-full px-3 py-2 bg-[#1a1a1a] border border-gray-600 text-white placeholder-gray-500 focus:outline-none focus:ring-[#FF5A00] focus:border-[#FF5A00] sm:text-sm disabled:opacity-50'
    : 'block w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-[#212121] focus:border-[#212121] sm:text-sm disabled:opacity-50'

  return (
    <div className="flex">
      <select
        value={dialCode}
        onChange={(e) => handleDialCodeChange(e.target.value)}
        disabled={disabled}
        aria-label={dialCodeLabel}
        className={`shrink-0 rounded-l-md px-2 py-2 text-sm ${selectClass}`}
      >
        {DIAL_CODES.map((entry) => (
          <option key={entry.code} value={entry.code}>
            {entry.flag} {entry.code}
          </option>
        ))}
      </select>
      <input
        type="tel"
        value={localNumber}
        onChange={(e) => handleLocalNumberChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete="tel-national"
        className={`rounded-r-md border-l-0 ${inputClass}`}
      />
    </div>
  )
}
